import { gql } from '@apollo/client'
import { produce } from 'immer'

import { User } from '~/lib/models/user'
import { Filter } from '~/types/filters'
import type { SelectTitleOption } from '~/types/ui'

import { RECURRING_STATUS_LABELS } from '../application-helpers/donations'
import { EnumMemberDonationFilter } from '../application-helpers/enums'
import { EnumLastMessageDirection } from '../application-helpers/model-enums'

const numericOperators: Filters.NumericOperator[] = [
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<='
]

export const valuelessOperators: Filters.Operator[] = [
  'is empty',
  'is not empty',
  'today',
  'this week'
]

const percentageOperators: Filters.PercentageOperator[] = [
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<='
]
// 'is not exactly' is deliberately absent. It exists in Filters.StringOperator
// and in the backend's constructWhereFragment because the opt-in Sources widget
// builds its "Other" drilldown as a flat list of exclusions, but it is not a
// generally useful operator to offer by hand: unlike the other negatives, which
// are NOT ILIKE and case-insensitive, it compiles to `!=` and is case-sensitive,
// so `is not exactly 'API'` silently excludes nothing when the stored value is
// 'api'. Filters arriving with it (a drilldown URL) still display and query
// correctly -- FilterRow builds the operator select's value from state.
export const stringOperators: Filters.StringOperator[] = [
  'contains',
  'does not contain',
  'is exactly',
  'similar to',
  'starts with',
  'does not start with',
  'ends with',
  'does not end with',
  'is empty',
  'is not empty'
]

export const listOperators: Filters.ListOperator[] = [
  'is',
  'is not',
  'is empty',
  'is not empty'
]

const booleanOperators: Filters.BooleanOperator[] = ['is']

export const groupOperators: Filters.GroupOperator[] = [
  'has any of',
  'has none of',
  'has all of'
]

export const errorOperators: Filters.ErrorOperator[] = [
  'matches any of',
  'matches none of'
]

const flowOperators: Filters.FlowOperator[] = [
  'has received',
  'has not received',
  'has received any of',
  'has not received any of',
  'has responded to any of',
  'has responded to none of',
  'is active in any of',
  'is not active in any of'
]

const broadcastOperators: Filters.BroadcastOperator[] = [
  'has received',
  'has not received',
  'has received any of',
  'has responded to any of',
  'has responded to none of'
]

const linkClickOperators: Filters.LinkClickOperator[] = [
  'has clicked',
  'has not clicked',
  'has clicked containing',
  'has not clicked containing'
]

const linkOperators: Filters.LinkOperator[] = [
  'has received',
  'has not received',
  'has received containing',
  'has not received containing'
]

const timezoneOperators: Filters.TimezoneOperator[] = [
  'is in',
  'is not in',
  'is empty',
  'is not empty'
]

const deviceTypeOperators: Filters.DeviceTypeOperator[] = ['is', 'is not']

const dateOperators: Filters.DateOperator[] = [
  'on',
  'before',
  'after',
  'in the last',
  'older than',
  'in the next',
  'days ago'
]

const birthdayOperators: Filters.BirthdayOperator[] = [
  'is empty',
  'on',
  'before',
  'after',
  'in month',
  'today',
  'this week'
]
const areaOperators: Filters.AreaOperator[] = [
  'within radius',
  'is',
  'is empty',
  'is not empty'
]

const callTargetOperators: Filters.CallTargetOperator[] = [
  'has called any of',
  'has not called any of'
]

const callNumberOperators: Filters.CallNumberOperator[] = [
  'has interacted with',
  'has not interacted with'
]

const senatorOperators: Filters.SenatorOperator[] = ['by party', 'by senator']

const stateSenatorOperators: Filters.StateSenatorOperator[] = [
  'by party',
  'by senator',
  'by upper district'
]

const representativeOperators: Filters.RepresentativeOperator[] = [
  'by party',
  'by representative',
  'by district'
]

const stateRepresentativeOperators: Filters.StateRepresentativeOperator[] = [
  'by party',
  'by representative',
  'by lower district'
]

const csvImportOperators: Filters.CsvImportOperator[] = [
  'in CSV',
  'in the last',
  'before',
  'after',
  'between'
]

const memberDonationOperators: Filters.MemberDonationOperator[] = [
  EnumMemberDonationFilter.byBroadcast,
  EnumMemberDonationFilter.between,
  EnumMemberDonationFilter.on,
  EnumMemberDonationFilter.notOn,
  EnumMemberDonationFilter.before,
  EnumMemberDonationFilter.notBefore,
  EnumMemberDonationFilter.after,
  EnumMemberDonationFilter.notAfter,
  EnumMemberDonationFilter.inTheLast,
  EnumMemberDonationFilter.notInTheLast,
  EnumMemberDonationFilter.olderThan,
  EnumMemberDonationFilter.daysAgo,
  EnumMemberDonationFilter.notDaysAgo
]

export type FilterData = {
  broadcasts: any
  flowData: any
  tzData: any
  targets: any
  callNumbers: any
  automatedMemberData: any
  organizerData: any
  campaignId: string | number
}

const assignmentOperators: Filters.AssignmentOperator[] = ['is', 'is not']

const accessGroupPermissionOperators = ['contains', 'does not contain']

const permissionOperator = [
  'has permission to view',
  'has permission to view/edit',
  'does not have permission to view',
  'does not have permission to view/edit'
]

const sentBroadcastOrAutomationOperator = [
  'broadcast',
  'automation',
  'broadcast or automation'
]

export const coreFields: Filters.FilterField[] = [
  { title: 'Phone Number', type: 'string', id: 'phoneNumber' },
  { title: 'Last Name', type: 'string', id: 'lastName' },
  { title: 'First Name', type: 'string', id: 'firstName' },
  {
    title: 'Subscribed',
    type: 'boolean',
    id: 'isSubscribed',
    advanced: {
      dateDescription: 'Opted Out',
      includeBroadcasts: false,
      includeFlows: false,
      hideSource: true,
      showOnlyWhenValue: 'false',
      allowedDateTypes: ['all time', 'in the last', 'from/to']
    }
  },
  { title: 'Muted', type: 'boolean', id: 'isMuted' },
  { title: 'Source', type: 'string', id: 'first_subscription_event' },
  { title: 'Email', type: 'string', id: 'email' },
  { title: 'Street Address', type: 'string', id: 'streetAddress' },
  { title: 'Zipcode', type: 'area', id: 'zipcode' },
  { title: 'City', type: 'string', id: 'city' },
  { title: 'State', type: 'string', id: 'state' },
  { title: 'Timezone', type: 'timezone', id: 'timezone' },
  { title: 'Device Type', type: 'device_type', id: 'device_type' },
  { title: 'Groups', type: 'group', id: 'groups' },
  {
    title: 'Broadcasts',
    type: 'broadcast',
    id: 'broadcast',
    advanced: {
      dateDescription: 'Messages Sent',
      includeBroadcasts: false,
      includeFlows: false,
      excludeOptions: ['has responded to any of', 'has responded to none of']
    }
  },
  {
    title: 'Flows',
    type: 'flow',
    id: 'flow',
    advanced: {
      dateDescription: 'Messages Received',
      includeBroadcasts: false,
      includeFlows: false,
      excludeOptions: ['is active in any of', 'is not active in any of']
    }
  },
  {
    title: 'Response Rate',
    type: 'percentage',
    id: 'responseRate',
    advanced: {
      dateDescription: 'Messages Received',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Click Rate',
    type: 'percentage',
    id: 'clickThroughRate',
    advanced: {
      dateDescription: 'Links Sent',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Call Rate',
    type: 'percentage',
    id: 'callRate',
    advanced: {
      dateDescription: 'Calls Made',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Has been sent any',
    type: 'sentBroadcastOrAutomation',
    id: 'sentBroadcastOrAutomation'
  },
  {
    title: 'Senators',
    type: 'federalSenator',
    id: 'federalSenator'
  },
  {
    title: 'Representative',
    type: 'federalRepresentative',
    id: 'federalRepresentative'
  },
  {
    title: 'State Senators',
    type: 'stateSenator',
    id: 'stateSenator'
  },
  {
    title: 'State Representative',
    type: 'stateRepresentative',
    id: 'stateRepresentative'
  },
  {
    title: 'Call Target',
    type: 'callTarget',
    id: 'callTarget',
    advanced: {
      dateDescription: 'Calls Made',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Call Number',
    type: 'callNumber',
    id: 'callNumber',
    advanced: {
      dateDescription: 'Calls Made',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Link Sent',
    type: 'linkOperator',
    id: 'linkOperator',
    advanced: {
      dateDescription: 'Links Sent',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Link Clicked',
    type: 'linkClicked',
    id: 'linkClicked',
    advanced: {
      dateDescription: 'Links Sent',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  {
    title: 'Sentiment',
    type: 'percentage',
    id: 'sentimentAvg',
    advanced: {
      dateDescription: 'Texts received',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  { title: 'Starred by Me', type: 'boolean', id: 'starredByCurrentUser' },
  {
    title: 'Last Message Direction',
    type: 'messageDirection',
    id: 'lastMessageDirection'
  },
  { title: 'Date Joined', type: 'date', id: 'createdAt' },
  // Registered because the opt-in Sources widget's drilldown emits it: every
  // period but "All time" adds a condition on this field -- 'after' plus
  // 'before' for the two periods with absolute bounds (yesterday, custom), and
  // a single 'in the last' for the four interval periods (72hours, week, month,
  // yearWeek). Both shapes render through the same lookup, so both need it.
  // Without an entry here FilterRow resolves the field to undefined, so the row
  // renders with a blank field name and an empty operator list, and re-picking
  // a field in it silently drops the date window the drilldown was built for.
  { title: 'Last Subscribed At', type: 'date', id: 'last_subscribed_at' },
  { title: 'Date of Birth', type: 'birthday', id: 'dateOfBirth' },
  { title: 'Age', type: 'numeric', id: 'derived_age' },
  { title: 'Carrier', type: 'string', id: 'mobileOperatorName' },
  {
    title: 'Msg Error Codes',
    type: 'error',
    id: 'errorCode',
    advanced: {
      dateDescription: 'Error occurred',
      includeBroadcasts: true,
      includeFlows: true
    }
  },
  { title: 'Assignment', type: 'assignment', id: 'organizerId' },

  {
    title: 'Synced Donations',
    type: 'memberDonationOperator',
    id: 'memberDonationOperator'
  },
  { title: 'Synced Average Donation', type: 'numeric', id: 'average_donation' },
  { title: 'Synced Donations Count', type: 'numeric', id: 'donations_count' },
  { title: 'Synced Highest Donation', type: 'numeric', id: 'highest_donation' },
  { title: 'Synced Last Donation At', type: 'date', id: 'last_donation_at' },
  {
    title: 'Synced Total Amount Donated',
    type: 'numeric',
    id: 'total_amount_donated'
  },
  {
    title: 'Synced Contribution Status',
    type: 'select',
    id: 'recurring_status',
    // `title` is the human label shown in the dropdown and the filter chip;
    // `value` stays the raw enum the query is built from.
    options: Object.entries(RECURRING_STATUS_LABELS).map(([value, title]) => ({
      value,
      title
    }))
  },
  { title: 'Imported', type: 'csvImport', id: 'csvImport' }
]

export const operators = {
  numeric: numericOperators.map((x) => ({ id: x, title: x })),
  number: numericOperators.map((x) => ({ id: x, title: x })),
  percentage: percentageOperators.map((x) => ({ id: x, title: x })),
  string: stringOperators.map((x) => ({ id: x, title: x })),
  text: stringOperators.map((x) => ({ id: x, title: x })),
  select: listOperators.map((x) => ({ id: x, title: x })),
  multiselect: groupOperators.map((x) => ({ id: x, title: x })),
  currency: numericOperators.map((x) => ({ id: x, title: x })),
  group: groupOperators.map((x) => ({ id: x, title: x })),
  error: errorOperators.map((x) => ({ id: x, title: x })),
  boolean: booleanOperators.map((x) => ({ id: x, title: x })),
  date: dateOperators.map((x) => ({ id: x, title: x })),
  birthday: birthdayOperators.map((x) => ({ id: x, title: x })),
  area: areaOperators.map((x) => ({ id: x, title: x })),
  broadcast: broadcastOperators.map((x) => ({ id: x, title: x })),
  flow: flowOperators.map((x) => ({ id: x, title: x })),
  timezone: timezoneOperators.map((x) => ({ id: x, title: x })),
  device_type: deviceTypeOperators.map((x) => ({ id: x, title: x })),
  assignment: assignmentOperators.map((x) => ({ id: x, title: x })),
  messageDirection: booleanOperators.map((x) => ({ id: x, title: x })),
  callTarget: callTargetOperators.map((x) => ({ id: x, title: x })),
  callNumber: callNumberOperators.map((x) => ({ id: x, title: x })),
  federalSenator: senatorOperators.map((x) => ({ id: x, title: x })),
  federalRepresentative: representativeOperators.map((x) => ({
    id: x,
    title: x
  })),
  stateSenator: stateSenatorOperators.map((x) => ({ id: x, title: x })),
  stateRepresentative: stateRepresentativeOperators.map((x) => ({
    id: x,
    title: x
  })),
  linkOperator: linkOperators.map((x) => ({ id: x, title: x })),
  linkClicked: linkClickOperators.map((x) => ({ id: x, title: x })),
  access_group: accessGroupPermissionOperators.map((x) => ({
    id: x,
    title: x
  })),
  campaign_permission: accessGroupPermissionOperators.map((x) => ({
    id: x,
    title: x
  })),
  group_permission: permissionOperator.map((x) => ({ id: x, title: x })),
  assignment_permission: permissionOperator.map((x) => ({ id: x, title: x })),
  field_permission: permissionOperator.map((x) => ({ id: x, title: x })),
  sentBroadcastOrAutomation: sentBroadcastOrAutomationOperator.map((x) => ({
    id: x,
    title: x
  })),
  memberDonationOperator: memberDonationOperators.map((x) => ({
    id: x,
    title: x
  })),
  csvImport: csvImportOperators.map((x) => ({ id: x, title: x }))
}

// Add or replace a filter with new values.  Returns a new object.
export const replaceFilter = produce((filters: Filter[], newFilter: Filter) => {
  const found = filters.findIndex((value) => value.field == newFilter.field)
  if (found < 0) filters.push(newFilter)
  else filters[found] = newFilter
})

export const filtersReducer = produce(
  (state, { type, index, value, fields, key, field, payload }) => {
    if (type === 'reset') {
      return payload
    }
    if (type === 'toggleActive') {
      state[index].excluded = !state[index].excluded
      return
    }
    if (type === 'removeAdvanced') {
      state.forEach((next) => (next.advanced = null))
      return
    }

    // TODO since we only debounce inputs where you can type,
    if (type !== 'add' && state[index] === undefined) return

    switch (type) {
      case 'add': {
        const field = fields[0]
        state.push({
          field: field.id,
          operator: operators[field.type][0].id
        })
        break
      }
      case 'remove': {
        state.splice(index, 1)
        break
      }
      case 'toggleAdvanced': {
        if (state[index].advanced) {
          state[index].advanced = null
        } else {
          state[index].advanced = {
            startDate: null,
            endDate: null,
            flows: [],
            broadcasts: []
          }
        }
        break
      }
      case 'setAdvanced': {
        if (!state[index].advanced) {
          state[index].advanced = {
            startDate: null,
            endDate: null,
            daysAgo: null,
            olderThan: null,
            last: null,
            flows: [],
            broadcasts: []
          }
        }
        if (key === 'daysAgo') {
          state[index].advanced.startDate = null
          state[index].advanced.endDate = null
          state[index].advanced.last = null
          state[index].advanced.olderThan = null
        }
        if (key === 'olderThan') {
          state[index].advanced.startDate = null
          state[index].advanced.endDate = null
          state[index].advanced.last = null
          state[index].advanced.daysAgo = null
        }
        if (key === 'last') {
          state[index].advanced.startDate = null
          state[index].advanced.endDate = null
          state[index].advanced.daysAgo = null
          state[index].advanced.olderThan = null
        }
        if (key === 'startDate' || key === 'endDate') {
          state[index].advanced.last = null
          state[index].advanced.daysAgo = null
          state[index].advanced.olderThan = null
        }

        state[index].advanced[key] = value

        break
      }
      case 'setField': {
        const field = fields.find(({ id }) => id === value)

        state[index].field = value
        state[index].value = null
        state[index].advanced = null
        let hasOperator = !state[index].operator
        let operatorValidForType =
          hasOperator &&
          (operators[field.type] as any).find(
            ({ id }) => id === state[index].operator
          )

        if (!hasOperator || !operatorValidForType) {
          state[index].operator = operators[field.type][0].id
        }
        if (field.type === 'field_permission') {
          state[index].value = field.type
        }
        if (field.type === 'assignment_permission') {
          state[index].value = field.type
        }
        if (field.type === 'boolean') {
          state[index].value = 'true'
        }
        if (field.type === 'birthday') {
          state[index].value = true
        }
        if (field.type === 'messageDirection') {
          state[index].value = EnumLastMessageDirection.incoming
        }
        if (field.type === 'select') {
          const firstOption = field?.options?.[0] || {}
          state[index].value = firstOption.value
        }
        if (field.type === 'multiselect') {
          state[index].value = []
        }
        if (field.type === 'assignment') {
          const firstOption = field?.options?.[0] || {}
          state[index].value = firstOption.value
        }
        break
      }
      case 'setOperator': {
        if (
          hasDifferentOperatorValueType(
            state[index].field,
            state[index].operator,
            value
          )
        ) {
          state[index].value = null
        }

        state[index].operator = value
        if (valuelessOperators.includes(value)) {
          state[index].value = 'true'
        }
        if (field?.advanced?.excludeOptions?.includes(value)) {
          state[index].advanced = null
        }
        const operatorField = fields?.find(
          ({ id }) => id === state[index].field
        )
        if (operatorField?.type === 'field_permission') {
          state[index].value = 'field_permission'
        }
        if (operatorField?.type === 'assignment_permission') {
          state[index].value = 'assignment_permission'
        }
        // A select always needs a concrete value. Toggling to a valueless
        // operator and back leaves 'true' or null behind, and filterToJsonEntry
        // drops any row without a value — so the filter silently stops applying
        // while its chip still shows one. Reseed only when what's there isn't a
        // real option, so a value the user picked survives an is/is not toggle.
        if (
          operatorField?.type === 'select' &&
          !valuelessOperators.includes(value) &&
          !operatorField.options?.some((o) => o.value === state[index].value)
        ) {
          state[index].value = operatorField.options?.[0]?.value
        }
        break
      }
      case 'setSecondaryOperator':
        state[index].value = null
        state[index].secondaryOperator = value
        break
      case 'setValue': {
        const currentField = fields?.find(({ id }) => id === state[index].field)
        if (
          currentField?.advanced?.showOnlyWhenValue &&
          value !== currentField.advanced.showOnlyWhenValue
        ) {
          state[index].advanced = null
        }
        state[index].value = value
        break
      }
    }
  }
)

const operatorValueTypeMap = {
  // boolean
  'is empty': 'boolean',
  'is not empty': 'boolean',
  // date
  after: 'date',
  [EnumMemberDonationFilter.after]: 'date',
  [EnumMemberDonationFilter.notAfter]: 'date',
  before: 'date',
  [EnumMemberDonationFilter.before]: 'date',
  [EnumMemberDonationFilter.notBefore]: 'date',
  on: 'date',
  [EnumMemberDonationFilter.on]: 'date',
  [EnumMemberDonationFilter.notOn]: 'date',
  // days
  'days ago': 'days',
  [EnumMemberDonationFilter.daysAgo]: 'days',
  [EnumMemberDonationFilter.notDaysAgo]: 'days',
  // days-interval
  'in the last': 'days-interval',
  [EnumMemberDonationFilter.inTheLast]: 'days-interval',
  [EnumMemberDonationFilter.notInTheLast]: 'days-interval',
  'in the next': 'days-interval',
  'older than': 'days-interval',
  [EnumMemberDonationFilter.olderThan]: 'days-interval',
  // month select
  'in month': 'month',
  // two-dates
  [EnumMemberDonationFilter.between]: 'two-dates'
}

// Helper function to determine if the operator change requires setting the value to null
export function hasDifferentOperatorValueType(
  field,
  existingOperator,
  newOperator
) {
  switch (field) {
    // always switch
    case 'csvImport':
    case 'federalRepresentative':
    case 'federalSenator':
    case 'sentBroadcastOrAutomation':
    case 'stateRepresentative':
    case 'stateSenator':
    case 'zipcode':
      return true
    // never switch
    case 'average_donation':
    case 'broadcast':
    case 'callRate':
    case 'callTarget':
    case 'clickThroughRate':
    case 'derived_age':
    case 'device_type':
    case 'donations_count':
    case 'errorCode':
    case 'flow':
    case 'groups':
    case 'highest_donation':
    case 'isSubscribed':
    case 'lastMessageDirection':
    case 'linkOperator':
    case 'linkClicked':
    case 'organizerId':
    case 'responseRate':
    case 'sentimentAvg':
    case 'starredByCurrentUser':
    case 'total_amount_donated':
      return false
    // switch on operator type (custom, dates, strings, special types)
    case 'city':
    case 'createdAt':
    case 'dateOfBirth':
    case 'email':
    case 'first_subscription_event':
    case 'firstName':
    case 'last_donation_at':
    case 'lastName':
    case 'memberDonationOperator':
    case 'mobileOperatorName':
    case 'phoneNumber':
    case 'state':
    case 'streetAddress':
    case 'timezone':
    default:
      return !!(
        (operatorValueTypeMap[existingOperator] ||
          operatorValueTypeMap[newOperator]) &&
        operatorValueTypeMap[existingOperator] !=
          operatorValueTypeMap[newOperator]
      )
  }
}

export const filtersFromJson = (json: any) => {
  if (!json) {
    return {
      combiner: 'and',
      filters: []
    }
  }

  return {
    combiner: json[0],
    filters: json[1].map(
      ([field, operator, value, advanced, secondaryOperator]) => ({
        field,
        operator,
        secondaryOperator,
        value: typeof value === 'boolean' ? JSON.stringify(value) : value,
        advanced
      })
    )
  }
}

function getFieldSpecificEntry(
  field: string,
  operator: string,
  value: any
): any[] | false {
  const singleValue = typeof value === 'object' ? value[0] : value
  const organizerVal = singleValue === 0 ? null : singleValue

  if (field === 'organizerId' && ['is', 'is not'].includes(operator)) {
    return [field, operator, organizerVal]
  }
  if (
    field === 'zipcode' &&
    valuelessOperators.includes(operator as Filters.Operator)
  ) {
    return [field, operator]
  }
  if (
    field === 'dateOfBirth' &&
    typeof value === 'object' &&
    typeof value.getDate === 'function'
  ) {
    return [field, operator, value.toISOString().substring(0, 10)]
  }
  if (field === 'zipcode' && ['is', 'is not'].includes(operator)) {
    return [field, operator, singleValue]
  }
  if (field === 'device_type' && ['is', 'is not'].includes(operator)) {
    return [field, operator, singleValue]
  }
  return false
}

function isMissingRequiredValues(
  field: string,
  value: any,
  secondaryOperator: string | undefined,
  operator?: string
): boolean {
  if (
    field === 'zipcode' &&
    value !== 'field_permission' &&
    (value?.length !== 2 || !value?.[0] || !value?.[1])
  )
    return true
  const missingDateRange = !Array.isArray(value) || !value[0] || !value[1]
  if (secondaryOperator === 'between_date_range' && missingDateRange)
    return true
  if (field === 'csvImport' && operator === 'between' && missingDateRange)
    return true
  return false
}

const filterToJsonEntry = ({
  field,
  operator,
  secondaryOperator,
  value,
  advanced
}: any) => {
  if (!field || !operator || (!value && value !== 0)) return
  const specific = getFieldSpecificEntry(field, operator, value)
  if (specific) return specific
  if (isMissingRequiredValues(field, value, secondaryOperator, operator)) return
  const isCustom = field.startsWith('custom.')
  if (
    !isCustom &&
    operator === 'is' &&
    !['lastMessageDirection', 'linkClicked', 'recurring_status'].includes(field)
  ) {
    value = value === 'true'
  }
  if (field === 'sentBroadcastOrAutomation' && !secondaryOperator) return
  return secondaryOperator
    ? [field, operator, value, advanced, secondaryOperator]
    : [field, operator, value, advanced]
}

export const filtersToJson = (
  filters: Filter[],
  combiner: Filters.Combiner
) => {
  filters = filters.filter(
    ({ field, operator, value, excluded }) =>
      field != null &&
      operator != null &&
      value != null &&
      !excluded &&
      (!Array.isArray(value) || value.length > 0)
  )
  if (!filters.length) return
  return [combiner, filters.map(filterToJsonEntry).filter(Boolean)]
}

export const getSenatorItemsGivenOpertor = (
  filter: Filter,
  data: {
    senators: SelectTitleOption[]
    parties: SelectTitleOption[]
  }
) => {
  if (filter.operator === 'by party') {
    return data?.parties || []
  }
  return data?.senators || []
}

export const getRepresentativeItemsGivenOpertor = (
  filter: Filter,
  data: {
    representatives: SelectTitleOption[]
    parties: SelectTitleOption[]
    districts: SelectTitleOption[]
  }
) => {
  if (filter.operator === 'by party') {
    return data?.parties || []
  } else if (filter.operator === 'by district') {
    return data?.districts || []
  }
  return data?.representatives || []
}

export const getStateSenatorsGivenOperator = (
  filter: Filter,
  data: {
    stateSenators: SelectTitleOption[]
    parties: SelectTitleOption[]
    stateUpperDistricts: SelectTitleOption[]
  }
) => {
  if (filter.operator === 'by party') {
    return data?.parties || []
  } else if (filter.operator === 'by upper district') {
    return data?.stateUpperDistricts || []
  }
  return data?.stateSenators || []
}

export const getStateRepresentativesGivenOperator = (
  filter: Filter,
  data: {
    stateRepresentatives: SelectTitleOption[]
    parties: SelectTitleOption[]
    stateLowerDistricts: SelectTitleOption[]
  }
) => {
  if (filter.operator === 'by party') {
    return data?.parties || []
  } else if (filter.operator === 'by lower district') {
    return data?.stateLowerDistricts || []
  }
  return data?.stateRepresentatives || []
}

export const getMembersUrlWithFilters = (campaignId, filters) => {
  if (!filters) return `/campaigns/${campaignId}/members`
  const filter = encodeURIComponent(
    JSON.stringify(filtersToJson(filters, 'and'))
  )
  return `/campaigns/${campaignId}/members?filters=${filter}`
}

export const getMembersUrlWithFiltersAddingFilter = (
  campaignId,
  filters,
  addFilter: Filter
) => {
  if (!filters) filters = []
  let newFilters = replaceFilter(filters, addFilter)
  return getMembersUrlWithFilters(campaignId, newFilters)
}

export const GET_ERROR_CODE_VALUES_QUERY = gql`
  query providerStatusCodes {
    providerStatusCodes(orderBy: STATUS_CODE_ASC) {
      nodes {
        id: statusCode
      }
    }
  }
`

export const formatUserData = (userData: Array<User>) => {
  if (userData) {
    const filteredUserData = userData.filter(
      (user) => user.archived_at === null
    )
    filteredUserData.sort((a, b) => Number(b.isAdmin) - Number(a.isAdmin))
    let formattedUserData = filteredUserData.map((user) => {
      return {
        id: user.id,
        title: `${user.isAdmin ? 'Admin' : 'Organizer'}, ${user.firstName} ${
          user.lastName
        }`
      }
    })
    formattedUserData.unshift({ id: 0, title: 'Unassigned' })
    return formattedUserData
  }
}

export const formatStatusCodes = (data: any) => {
  let formatCodes = data?.providerStatusCodes?.nodes || []
  const seen = []
  formatCodes = formatCodes.filter((obj) => {
    if (!seen.includes(obj.id)) {
      seen.push(obj.id)
      return true
    }
    return false
  })
  formatCodes = formatCodes.map((next) => {
    return { title: `${next.id}`, id: next.id }
  })
  return formatCodes
}

export function filterValueForDate(operator, value): string {
  if (!operator) return ''
  const dateType = filterOperatorTypeForDate(operator)

  if (dateType === 'daysOnly' || dateType === 'interval') return value

  if (value) return new Date(value).toLocaleDateString()
  return null
}

export function filterOperatorTypeForDate(operator) {
  if (
    operator === 'days ago' ||
    [
      EnumMemberDonationFilter.daysAgo,
      EnumMemberDonationFilter.notDaysAgo
    ].includes(operator)
  ) {
    return 'daysOnly'
  } else if (
    operator.startsWith('in the') ||
    [
      EnumMemberDonationFilter.inTheLast,
      EnumMemberDonationFilter.notInTheLast
    ].includes(operator)
  ) {
    return 'interval'
  } else if (
    operator === 'older than' ||
    operator === EnumMemberDonationFilter.olderThan
  ) {
    return 'interval'
  } else if (operator.startsWith('in month')) {
    return 'month'
  } else if (operator.startsWith('today')) {
    return 'today'
  } else if (operator.startsWith('is empty')) {
    return 'is empty'
  }
  return 'date'
}

export function filterOperatorDisplay(operator) {
  if (operator === 'days ago') {
    return 'ago'
  }
  return operator
}

export const defaultAutomatedMemberFields = {
  districts: [],
  parties: [],
  representatives: [],
  senators: [],
  stateSenators: [],
  stateRepresentatives: []
}

export function getDateRangeError(
  dateType: string,
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined
): string | null {
  if (dateType !== 'from/to') return null
  if (!startDate && !endDate) return 'From and to dates are required'
  if (startDate && !endDate) return 'To date is required'
  if (!startDate && endDate) return 'From date is required'
  if (
    startDate &&
    endDate &&
    new Date(startDate).getTime() >= new Date(endDate).getTime()
  )
    return 'From date must be before to date'
  return null
}

export const filtersExcludedFromDynamicGroups = ['starredByCurrentUser']
export function buildNonDynamicGroupFilters(filters: any[]) {
  return filters
    .filter((next) => {
      return filtersExcludedFromDynamicGroups.includes(next[0])
    })
    .map((next) => {
      const coreField = coreFields.find((f) => f.id === next[0])
      return coreField.title
    })
    .join(', ')
}
